import React from 'react';
import {
  ArrayInput,
  BooleanInput,
  DateInput,
  NumberInput,
  TextInput
} from 'react-admin';
import { Divider, Grid, Typography } from '@mui/material';
import { RepeatableInput } from '../components/ra-forms/RepeatableInput.tsx';

const OpportunityProfileForm: React.FC = () => {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        General Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="legalEntityName" label="Legal Entity Name" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="dba" label="DBA" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="entityType" label="Entity Type" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="fein" label="FEIN" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="yearEstablished" label="Year Established" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="website" label="Website" helperText={false} fullWidth />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
        Ownership
      </Typography>
      <Grid
        size={{ xs: 12 }}
        sx={{
          '& .RaArrayInput-root .MuiFormHelperText-root': {
            display: 'none'
          }
        }}
      >
        <ArrayInput source="owners">
          <RepeatableInput addLabel="Add owner" removeLabel="Remove owner" inline>
            <TextInput source="name" label="Name" helperText={false} />
            <NumberInput source="percentage" label="Percent" helperText={false} />
          </RepeatableInput>
        </ArrayInput>
      </Grid>

      <Grid container spacing={2} mt={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="primaryContactName" label="Primary Contact Name" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="primaryContactTitle" label="Title" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="primaryContactEmail" label="Email" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="primaryContactPhone" label="Phone" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="businessOwnerName" label="Business Owner Name" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DateInput source="businessOwnerDob" label="Business Owner DOB" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput
            source="businessOwnerAddress"
            label="Business Owner Address"
            helperText={false}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput source="mailingAddress" label="Mailing Address" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="numberOfLocations" label="Number of Locations" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="statesOfOperation" label="States of Operation" helperText={false} fullWidth />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
        Business Physical Addresses
      </Typography>
      <Grid
        size={{ xs: 12 }}
        sx={{
          '& .RaArrayInput-root .MuiFormHelperText-root': {
            display: 'none'
          }
        }}
      >
        <ArrayInput source="locations">
          <RepeatableInput addLabel="Add location" removeLabel="Remove location" inline>
            <TextInput source="addressLine" label="Address" helperText={false} fullWidth />
          </RepeatableInput>
        </ArrayInput>
      </Grid>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
        Additional Legal Entities
      </Typography>
      <Grid
        size={{ xs: 12 }}
        sx={{
          '& .RaArrayInput-root .MuiFormHelperText-root': {
            display: 'none'
          }
        }}
      >
        <ArrayInput source="entities">
          <RepeatableInput addLabel="Add legal entity" removeLabel="Remove entity" itemLabel="Entity">
            <TextInput source="legalEntityName" label="Legal Entity Name" helperText={false} fullWidth />
            <TextInput source="dba" label="DBA" helperText={false} fullWidth />
            <TextInput source="entityType" label="Entity Type" helperText={false} fullWidth />
            <TextInput source="fein" label="FEIN" helperText={false} fullWidth />
            <NumberInput source="yearEstablished" label="Year Established" helperText={false} fullWidth />
            <TextInput
              source="ownershipDetails"
              label="Ownership Details"
              helperText={false}
              fullWidth
              multiline
              rows={2}
            />
          </RepeatableInput>
        </ArrayInput>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Business Operations
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextInput
            source="detailedDescription"
            label="Detailed Description of Operations"
            helperText={false}
            fullWidth
            multiline
            rows={3}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="naicsCode" label="NAICS Code" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="sicCode" label="SIC Code" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="annualRevenue" label="Annual Revenue" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="projectedRevenue" label="Projected Revenue (12 Mo)" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="employeesFullTime" label="Employees (Full-Time)" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="employeesPartTime" label="Employees (Part-Time)" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="estimatedAnnualPayroll" label="Estimated Payroll" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput
            source="subcontractedRevenuePercent"
            label="% Revenue Subcontracted"
            helperText={false}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="independentContractorsUsed" label="Independent Contractors Used" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="workOutsideUs" label="Work Outside the U.S." />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="manufacturesProducts" label="Manufacture/Distribute/Import Products" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="professionalServicesProvided" label="Professional Services Provided" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="patientCareProvided" label="Patient Care Provided" />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Lines of Coverage Requested
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageGeneralLiability" label="General Liability" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageProperty" label="Property" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageCommercialAuto" label="Commercial Auto (Owned)" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageHiredNonOwnedAuto" label="Hired/Non-Owned Auto" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageWorkersComp" label="Workers Compensation" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageUmbrellaExcess" label="Umbrella/Excess" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageProfessionalLiability" label="Professional Liability (E&O)" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageCyber" label="Cyber" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageDO" label="D&O" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageEPLI" label="EPLI" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageCrime" label="Crime" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageInlandMarine" label="Inland/Ocean Marine/Transit" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageIntellectualProperty" label="Intellectual Property" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageCreditInsurance" label="Credit Insurance" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageFlood" label="Flood" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coveragePollution" label="Pollution Liability" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageBuildersRisk" label="Builders Risk" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageForeignTravel" label="Foreign Travel" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageForeignLiability" label="Foreign Liability" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageVolunteerAccident" label="Volunteer Accident" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageBond" label="Bond" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="coverageLiquorLiability" label="Liquor Liability" />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput source="coverageOther" label="Other" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput source="desiredLimits" label="Desired Limits" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput
            source="reasonForMarketing"
            label="Reason for Marketing"
            helperText={false}
            fullWidth
            multiline
            rows={3}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="priorClaims" label="Prior claims in last 5 years" />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Property Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="propertyOwnerType" label="Building Owner or Tenant" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="constructionType" label="Construction Type" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="yearBuilt" label="Year Built" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="buildingSquareFootage" label="Sq Ft Building" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="occupiedSquareFootage" label="Sq Ft Occupied" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="roofTypeAge" label="Roof Type & Age" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput source="hvacUpdates" label="Electrical/Plumbing/HVAC Updates" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="sprinklered" label="Sprinklered" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="fireAlarmCentralStation" label="Fire Alarm Central Station" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="burglarAlarmCentralStation" label="Burglar Alarm Central Station" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="businessPersonalPropertyValue" label="BPP Value" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput source="buildingValue" label="Building Value" helperText={false} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="businessIncomeExposure" label="Business Income Exposure" />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Documents to Request
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docCurrentPolicies" label="Current Copies of Policies" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docShareholderAgreements" label="Shareholder/Investor Agreements" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docAutomobileSchedule" label="Automobile Schedule" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docFiveYearLossRuns" label="Five-Year Loss Runs" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docMasterServiceAgreements" label="Master Service Agreements" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docDriversList" label="Drivers List" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docCompletedApplications" label="Completed Applications" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docStatementsOfWork" label="Statements of Work" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docOrganizationalChart" label="Organizational Chart" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docIndependentContractorAgreements" label="Independent Contractor Agreements" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docExecutiveSummary" label="Executive Summary" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docSubcontractorAgreements" label="Subcontractor Agreements" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docPitchDecks" label="Pitch Decks" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docVendorAgreements" label="Vendor Agreements" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docResumesCVs" label="Resumes/CVs" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docStaffingAgreements" label="Staffing Agreements" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docCurrentFinancials" label="Current Financials" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docLeaseAgreements" label="Lease Agreements" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docProformaFinancials" label="Proforma Financials" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docManufacturingAgreements" label="Manufacturing Agreements" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docCapTable" label="Cap Table" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <BooleanInput source="docPropertyScheduleCope" label="Property Schedule with COPE" />
        </Grid>
      </Grid>
    </>
  );
};

export default OpportunityProfileForm;
